import type { SleepStage, SnapshotDrill } from "@healthos/shared";
import { useEffect, useMemo, useState } from "react";

const ROWS: ReadonlyArray<{ key: SleepStage; label: string }> = [
  { key: "deep", label: "Deep" },
  { key: "rem", label: "REM" },
  { key: "light", label: "Light" },
  { key: "awake", label: "Awake" },
];

function clock(at: string | number): string {
  const d = new Date(at);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function SnapshotHypnogram({
  hypnogram,
}: {
  hypnogram: NonNullable<SnapshotDrill["hypnogram"]>;
}) {
  // Grouped once: four rows over N samples would otherwise walk the night four
  // times and render three empty children for every filled one.
  const { span, bars } = useMemo(() => {
    const from = new Date(hypnogram.from).getTime();
    const to = new Date(hypnogram.to).getTime();
    const width = to - from;
    const grouped: Record<
      SleepStage,
      Array<{ n: number; left: number; width: number; at: number; next: number }>
    > = {
      deep: [],
      rem: [],
      light: [],
      awake: [],
    };
    if (!Number.isFinite(width) || width <= 0) return { span: 0, bars: grouped };
    const stamps = hypnogram.samples.map((s) => new Date(s.at).getTime());
    hypnogram.samples.forEach((sample, i) => {
      const at = stamps[i] ?? from;
      const next = stamps[i + 1] ?? to;
      grouped[sample.stage].push({
        at,
        next,
        n: i,
        left: ((at - from) / width) * 100,
        width: Math.max(((next - at) / width) * 100, 0.4),
      });
    });
    return { span: width, bars: grouped };
  }, [hypnogram]);
  // Hover previews a band; a click or Enter pins one, so the night is readable
  // without a pointer. Escape lets go.
  const [hovered, setHovered] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);
  const at = pinned ?? hovered;
  useEffect(() => {
    if (!pinned) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPinned(null);
    };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [pinned]);
  // The night as one sentence: what a member takes from a hypnogram without
  // reading it band by band.
  const summary = useMemo(() => {
    // Samples arrive one per bucket, so a run is however many of them sit end
    // to end: counting the longest single bucket reports the bucket size.
    const runs = (of: SleepStage) => {
      let longest = 0;
      let start: number | null = null;
      let end = 0;
      let count = 0;
      for (const bar of bars[of]) {
        if (start !== null && bar.at - end <= 1_000) {
          end = bar.next;
          continue;
        }
        if (start !== null) longest = Math.max(longest, end - start);
        count++;
        start = bar.at;
        end = bar.next;
      }
      if (start !== null) longest = Math.max(longest, end - start);
      return { longest, count };
    };
    const wakes = runs("awake").count;
    const deepMinutes = Math.round(runs("deep").longest / 60_000);
    if (wakes === 0 && deepMinutes === 0) return null;
    const parts = [];
    if (wakes > 0) parts.push(`${wakes} wake ${wakes === 1 ? "event" : "events"}`);
    if (deepMinutes > 0) parts.push(`longest deep run ${deepMinutes}m`);
    return parts.join(" · ");
  }, [bars]);
  if (span <= 0) return null;

  return (
    <section className="snap-card">
      <div className="snap-section-head">
        <h3>Last night</h3>
        {/* The band under the pointer, or the one pinned, takes the hint, so
            the night reads without counting rows against a legend. */}
        <span className={`hint${at ? " is-at" : ""}`} aria-live="polite">
          {at ?? summary ?? `${clock(hypnogram.from)} to ${clock(hypnogram.to)}`}
        </span>
        <span className="snap-hyp-key">
          {ROWS.map((row) => (
            <span key={row.key} className={`snap-hyp-kk is-${row.key}`}>
              <i />
              {row.label}
            </span>
          ))}
        </span>
      </div>
      <div className="snap-hyp" onPointerLeave={() => setHovered(null)}>
        {ROWS.map((row) => (
          <div key={row.key} className={`snap-hyp-row is-${row.key}`}>
            <span className="snap-hyp-lab">{row.label}</span>
            <span className="snap-hyp-track">
              {bars[row.key].map((bar) => {
                const says = `${row.label} · ${clock(bar.at)} to ${clock(bar.next)}`;
                return (
                  <button
                    key={`${row.key}-${bar.n}`}
                    type="button"
                    className={pinned === says ? "is-pinned" : undefined}
                    style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
                    aria-label={says}
                    aria-pressed={pinned === says}
                    onPointerEnter={() => setHovered(says)}
                    onFocus={() => setHovered(says)}
                    onBlur={() => setHovered(null)}
                    onClick={() => setPinned((p) => (p === says ? null : says))}
                  />
                );
              })}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function SnapshotZones({ zones }: { zones: NonNullable<SnapshotDrill["zones"]> }) {
  const most = Math.max(...zones.map((z) => z.minutes), 1);
  const total = zones.reduce((sum, z) => sum + z.minutes, 0);
  const [at, setAt] = useState<string | null>(null);
  return (
    <section className="snap-card">
      <div className="snap-section-head">
        <h3>Where your heart rate sat</h3>
        <span className={`hint${at ? " is-at" : ""}`} aria-live="polite">
          {at ?? "Minutes in each zone"}
        </span>
      </div>
      <div className="snap-zones" onPointerLeave={() => setAt(null)}>
        {zones.map((z) => {
          const says =
            total > 0
              ? `${z.label} · ${Math.round(z.minutes)} min, ${Math.round((z.minutes / total) * 100)}% of the session`
              : `${z.label} · ${Math.round(z.minutes)} min`;
          return (
            <button
              key={z.zone}
              type="button"
              className="snap-zone"
              style={{ ["--z" as string]: String(z.zone) }}
              aria-label={says}
              onPointerEnter={() => setAt(says)}
              onFocus={() => setAt(says)}
              onBlur={() => setAt(null)}
            >
              <span className="snap-zone-k">{z.label}</span>
              <span className="snap-zone-track">
                <i style={{ width: `${(z.minutes / most) * 100}%` }} />
              </span>
              <span className="snap-zone-v">{Math.round(z.minutes)} min</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
